import { MdoBasketUploadCheckItem } from '@epicuro-next/data/baskets';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Severity } from 'graphql-api/baskets-api-interfaces';

import {
  BasketMDOUploadErrors,
  UploadBasketItemErrorType,
} from '../../models/basket-mdo-upload.models';
import {
  State,
  uploadBasketFeatureKey,
} from '../reducers/upload-basket.reducer';

export const selectUploadBasketState = createFeatureSelector<State>(
  uploadBasketFeatureKey,
);

export const selectSdoValidationResults = createSelector(
  selectUploadBasketState,
  (state) => state.sdoValidationResults,
);

export const selectUploadSDOWarnings = createSelector(
  selectSdoValidationResults,
  (sdoValidationResults) =>
    sdoValidationResults?.map((validation) => {
      const invalidEANs = validation.values
        ?.filter((ean) => !validation.message?.includes(ean))
        ?.join(', ');
      return {
        ...validation,
        message: invalidEANs
          ? `${validation.message}: ${invalidEANs}`
          : validation.message,
        severity: Severity.Warning,
      };
    }),
);

export const selectIsUploadingSDO = createSelector(
  selectUploadBasketState,
  (state) => state.isSDOUploading,
);

export const selectIsMDOBasketChecking = createSelector(
  selectUploadBasketState,
  (state) => state.isMDOChecking,
);

export const selectIsMDOBasketImporting = createSelector(
  selectUploadBasketState,
  (state) => state.isMDOImporting,
);

export const selectMDOUploadCheckResult = createSelector(
  selectUploadBasketState,
  (state) => state.mdoUploadCheckResult,
);

export const selectIsInvalidMDOFile = createSelector(
  selectMDOUploadCheckResult,
  (checkResult) => checkResult?.invalidCsvFile,
);

export const selectMDOFileItemsErrors = createSelector(
  selectMDOUploadCheckResult,
  (checkResult) => checkResult?.items,
);

export const selectMDOItemsGroupedErrors = createSelector(
  selectMDOFileItemsErrors,
  (items): BasketMDOUploadErrors | undefined =>
    items?.reduce(
      (errors: BasketMDOUploadErrors, item) => {
        const rowNumber = item.rowNumber;

        return {
          [UploadBasketItemErrorType.INVALID_QUANTITY]: errors[
            UploadBasketItemErrorType.INVALID_QUANTITY
          ].concat(isInvalidQuantity(item) ? rowNumber : []),
          [UploadBasketItemErrorType.INVALID_WEEK]: errors[
            UploadBasketItemErrorType.INVALID_WEEK
          ].concat(item.invalidWeek ? rowNumber : []),
          [UploadBasketItemErrorType.INVALID_DATE]: errors[
            UploadBasketItemErrorType.INVALID_DATE
          ].concat(item.invalidDate ? rowNumber : []),
          [UploadBasketItemErrorType.INVALID_ADDRESS]: errors[
            UploadBasketItemErrorType.INVALID_ADDRESS
          ].concat(isInvalidAddress(item) ? rowNumber : []),
          [UploadBasketItemErrorType.INVALID_PRODUCTS]: errors[
            UploadBasketItemErrorType.INVALID_PRODUCTS
          ].concat(item.invalidProduct ? rowNumber : []),
          [UploadBasketItemErrorType.INVALID_ORDER_TYPE]: errors[
            UploadBasketItemErrorType.INVALID_ORDER_TYPE
          ].concat(item.invalidOrderType ? rowNumber : []),
        };
      },
      {
        [UploadBasketItemErrorType.INVALID_DATE]: [],
        [UploadBasketItemErrorType.INVALID_QUANTITY]: [],
        [UploadBasketItemErrorType.INVALID_WEEK]: [],
        [UploadBasketItemErrorType.INVALID_ADDRESS]: [],
        [UploadBasketItemErrorType.INVALID_PRODUCTS]: [],
        [UploadBasketItemErrorType.INVALID_ORDER_TYPE]: [],
      },
    ),
);

export const selectMDOItemErrorsCount = createSelector(
  selectMDOItemsGroupedErrors,
  (errors) =>
    Object.values(errors ?? {}).reduce(
      (sum, invalidRows) => sum + invalidRows.length,
      0,
    ),
);

export const selectHasMDOItemsErrors = createSelector(
  selectMDOUploadCheckResult,
  (checkResult) => !checkResult?.isValid,
);

export const selectMDOErrorsCount = createSelector(
  selectIsInvalidMDOFile,
  selectMDOItemErrorsCount,
  (isInvalid, itemsCount) => (isInvalid ? 1 : itemsCount),
);

export const selectMDOFileToken = createSelector(
  selectMDOUploadCheckResult,
  (checkResults) => checkResults?.fileToken,
);

const isInvalidQuantity = (item: MdoBasketUploadCheckItem) =>
  item.invalidQuantity || item.invalidAddresses?.some((i) => i.invalidQuantity);
const isInvalidAddress = (item: MdoBasketUploadCheckItem) =>
  item.invalidAddresses?.some((i) => i.invalidAddress);
